import React, { useState, useEffect, useRef, useLayoutEffect, useMemo, useCallback } from 'react';
import Measure from "./Measure";
import Note from "./Note";
import LaneLine from "./LaneLine";
import { SkillActivation } from "../types/optimizer";

// --- Configuration ---
const MIN_LANE_WIDTH = 40; 
const MOBILE_MIN_LANE_WIDTH = 26;
const MOBILE_MIN_PIXELS_PER_SECOND = 24;
const LANE_COUNT = 5;     
const SIDE_MARGIN = 6; 
const DENSITY_BAR_WIDTH = 6; 
const DENSITY_BAR_GAP = 2;   

const FIXED_COL_WIDTH_OVERHEAD = SIDE_MARGIN + DENSITY_BAR_WIDTH + DENSITY_BAR_GAP + SIDE_MARGIN;
const MIN_COLUMN_WIDTH = FIXED_COL_WIDTH_OVERHEAD + (LANE_COUNT * MIN_LANE_WIDTH);
const CONTENT_OFFSET_X = SIDE_MARGIN + DENSITY_BAR_WIDTH + DENSITY_BAR_GAP;


// --- Density Bar Component (Local) ---
function DensityBar({ startTime, endTime, scale, data, maxDensity, densityWindow }: any) {
    const width = DENSITY_BAR_WIDTH;
    const step = 0.5;
    const rects = [];
    const totalDuration = endTime - startTime;
    const numSteps = Math.ceil(totalDuration / step);

    for (let i = 0; i < numSteps; i++) {
        const t = startTime + (i * step);
        const count = data.filter((n: any) => n.JudgeTime > t - densityWindow && n.JudgeTime <= t).length;
        const ratio = Math.min(1, count / maxDensity);
        const hue = 120 * (1 - ratio); 
        const color = `hsl(${hue}, 80%, 50%)`;

        const yBottom = (endTime - t) * scale;
        const nextT = Math.min(endTime, t + step);
        const yTop = (endTime - nextT) * scale;
        const height = Math.abs(yBottom - yTop);

        rects.push(
            <rect key={t} x={SIDE_MARGIN} y={yTop} width={width} height={height + 0.5} fill={color} shapeRendering="crispEdges" />
        );
    }
    return <g><rect x={SIDE_MARGIN} y={0} width={width} height={(endTime - startTime) * scale} fill="#e5e7eb" />{rects}</g>;
}

// --- Memoized Static Layer ---
const NotesLayer = React.memo((props: any) => {
    const { col, containerHeight, layoutMetrics, loadedChart, loadedMeasure, maxDensity, densityWindow, colors, onHover, onLeave } = props;
    
    return (
        <>
            <DensityBar startTime={col.start} endTime={col.end} scale={col.scale} data={loadedChart} maxDensity={maxDensity} densityWindow={densityWindow} />
            <LaneLine height={containerHeight} laneWidth={layoutMetrics.laneWidth} contentOffsetX={layoutMetrics.contentOffsetX} />
            
            {/* Measures */}
            {loadedMeasure.filter((t: number) => t >= col.start && t < col.end).map((m: number) => (
                <Measure key={m} time={m} measureCount={loadedMeasure.indexOf(m)+1} columnEndTime={col.end} scale={col.scale} laneWidth={layoutMetrics.laneWidth} contentOffsetX={layoutMetrics.contentOffsetX}/>
            ))}

            {/* Notes */}
            {loadedChart.filter((n: any) => n.JudgeTime >= col.start && n.JudgeTime < col.end).map((n: any, i: number) => (
                <Note key={i} noteProp={n} columnEndTime={col.end} scale={col.scale} laneWidth={layoutMetrics.laneWidth} contentOffsetX={layoutMetrics.contentOffsetX} count={loadedChart.indexOf(n)} color={colors[n.CastIndex]} onHover={onHover} onLeave={onLeave}/>
            ))}
        </>
    );
});


// --- Main ChartDetail Component ---
function ChartDetail(props: any) {
    const [loadedChart, setLoadedChart] = useState<any[]>([]);
    const [loadedMeasure, setLoadedMeasure] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const containerRef = useRef<HTMLDivElement>(null);
    const [containerHeight, setContainerHeight] = useState(600);
    const [columns, setColumns] = useState<any[]>([]);
    const [hoverTooltip, setHoverTooltip] = useState<{x: number, y: number, content: any} | null>(null);
    
    const [layoutMetrics, setLayoutMetrics] = useState({
        colWidth: MIN_COLUMN_WIDTH,
        laneWidth: MIN_LANE_WIDTH,
        contentOffsetX: CONTENT_OFFSET_X
    });

    // Data Fetching
    useEffect(() => {
        setIsLoading(true);
        setLoadedChart([]);
        setLoadedMeasure([]);
        const controller = new AbortController();
        const loadJson = (url: string) => fetch(url, { signal: controller.signal }).then(response => {
            if (!response.ok) throw new Error(`Unable to load chart (HTTP ${response.status})`);
            return response.json();
        });
        const baseId = props.id.split('?')[0]; 
        
        Promise.all([
            loadJson(`${process.env.PUBLIC_URL}/charts/${baseId.toLowerCase()}.json`),
            loadJson(`${process.env.PUBLIC_URL}/charts/${baseId.toLowerCase()}_measures.json`)
        ]).then(([chartData, measureData]) => {
            if (controller.signal.aborted) return;
            setLoadedChart(chartData);
            setLoadedMeasure(measureData);
            setIsLoading(false);
        }).catch(err => {
            if (controller.signal.aborted) return;
            console.error("Error loading chart data", err);
            setIsLoading(false);
        });
        return () => controller.abort();
    }, [props.id]);

    // Max Density Calculation
    const maxDensity = useMemo(() => {
        if (loadedChart.length === 0) return 1;
        let max = 0;
        const lastTime = loadedChart[loadedChart.length - 1].JudgeTime;
        for (let t = 0; t <= lastTime; t += 0.5) {
            const count = loadedChart.filter((n: any) => n.JudgeTime > t - props.densityWindow && n.JudgeTime <= t).length;
            if (count > max) max = count;
        }
        return Math.max(1, max);
    }, [loadedChart, props.densityWindow]);

    // Layout Calculation
    useLayoutEffect(() => {
        const calculateLayout = () => {
            if (containerRef.current && loadedMeasure.length > 0) {
                const availableHeight = containerRef.current.clientHeight;
                const availableWidth = containerRef.current.clientWidth;
                const isMobile = window.matchMedia('(max-width: 767px)').matches;
                const fitWidth = Math.max(100, availableWidth - 20); 
                const maxCols = isMobile ? 2 : Math.max(1, Math.floor(fitWidth / MIN_COLUMN_WIDTH));
                const lastMeasureTime = loadedMeasure[loadedMeasure.length - 1];
                const targetDurationPerCol = lastMeasureTime / maxCols;
                // Give mobile charts at least two screens of height and keep long songs readable.
                const fitHeight = isMobile
                    ? Math.max(1200, availableHeight * 2, targetDurationPerCol * MOBILE_MIN_PIXELS_PER_SECOND / 0.9)
                    : Math.max(400, availableHeight);

                setContainerHeight(fitHeight);

                // Keep lanes readable on very narrow screens by allowing horizontal scrolling.
                const minColWidth = isMobile
                    ? FIXED_COL_WIDTH_OVERHEAD + LANE_COUNT * MOBILE_MIN_LANE_WIDTH
                    : MIN_COLUMN_WIDTH;
                const dynamicColWidth = Math.max(minColWidth, Math.floor(fitWidth / maxCols));
                const dynamicLaneWidth = (dynamicColWidth - FIXED_COL_WIDTH_OVERHEAD) / LANE_COUNT;
                
                // Vertical Scale
                const baseScale = fitHeight / targetDurationPerCol;
                const adjustedScale = baseScale * 0.9; 

                setLayoutMetrics({
                    colWidth: dynamicColWidth,
                    laneWidth: dynamicLaneWidth,
                    contentOffsetX: CONTENT_OFFSET_X
                });

                // Column Splitting
                const newColumns = [];
                let chunkStartTime = 0;
                let measureIndex = 0;

                while (chunkStartTime < lastMeasureTime) {
                    let bestEndTime = chunkStartTime;
                    
                    // Find split point
                    for (let i = measureIndex; i < loadedMeasure.length; i++) {
                        const mTime = loadedMeasure[i];
                        const heightNeeded = (mTime - chunkStartTime) * adjustedScale;
                        if (heightNeeded <= fitHeight) {
                            bestEndTime = mTime;
                            measureIndex = i; 
                        } else {
                            break; 
                        }
                    }

                    if (bestEndTime <= chunkStartTime) {
                         if(measureIndex < loadedMeasure.length - 1) {
                             measureIndex++;
                             bestEndTime = loadedMeasure[measureIndex];
                         } else {
                             bestEndTime = lastMeasureTime;
                             if (bestEndTime <= chunkStartTime) break; 
                         }
                    }

                    newColumns.push({ start: chunkStartTime, end: bestEndTime, scale: adjustedScale });
                    chunkStartTime = bestEndTime;
                    if (chunkStartTime >= lastMeasureTime) break;
                }
                setColumns(newColumns);
            }
        };

        const observer = new ResizeObserver(calculateLayout);
        if (containerRef.current) observer.observe(containerRef.current);
        window.addEventListener('resize', calculateLayout);
        if (!isLoading) calculateLayout();
        return () => { observer.disconnect(); window.removeEventListener('resize', calculateLayout); };
    }, [isLoading, loadedMeasure, loadedChart]);

    useEffect(() => {
        if (!props.selectedSkill) return;
        const index = columns.findIndex(col => props.selectedSkill.time >= col.start && props.selectedSkill.time < col.end);
        const container = containerRef.current;
        if (index >= 0 && container) container.scrollTo({
            left: index * layoutMetrics.colWidth,
            top: Math.max(0, (columns[index].end - props.selectedSkill.time) * columns[index].scale - container.clientHeight / 2),
            behavior: 'smooth',
        });
    }, [props.selectedSkill, columns, layoutMetrics.colWidth]);

    // Tooltip - Memoized with dependencies restored for detailed content
    const handleNoteHover = useCallback((e: React.MouseEvent, note: any) => {
        const t = note.JudgeTime;
        const count = loadedChart.filter((n: any) => n.JudgeTime > t - props.densityWindow && n.JudgeTime <= t).length;
        const index = loadedChart.indexOf(note) + 1;
        const skills: SkillActivation[] = (props.activations ?? []).filter((a: SkillActivation) => a.note_index === index - 1);
        
        const content = (
            <div>
                <div className="font-bold">Note #{index} / {note.JudgeTime.toFixed(2)}s</div>
                {skills.map((a, i) => <div key={i}>{a.skill} #{a.copy} · {a.phase} this note · {a.time.toFixed(2)}s</div>)}
                <div className="text-[10px] text-gray-300 mt-1">{count} notes / prev {props.densityWindow}s</div>
            </div>
        );
        setHoverTooltip({ x: e.clientX, y: e.clientY, content });
    }, [loadedChart, props.densityWindow, props.activations]); 

    const handleLeave = useCallback(() => setHoverTooltip(null), []);

    if (isLoading) return <div className="flex w-full justify-center my-10">Loading Chart...</div>;

    return (
        <div ref={containerRef} className='flex flex-row overflow-auto border-t border-b border-gray-200 w-full h-full'>
            <div className="flex shrink-0 mx-auto" style={{ height: containerHeight }}>
                {columns.map((col, index) => (
                    <div key={index} className="relative border-r border-gray-300 bg-white" style={{ height: containerHeight, width: layoutMetrics.colWidth, flexShrink: 0 }}>
                        <div className="absolute top-0 right-1 text-[9px] text-gray-300 font-mono">{col.end.toFixed(1)}s</div>
                        <svg width="100%" height={containerHeight} className="block">
                            
                            {/* Render Static Layer (Memoized) */}
                            <NotesLayer 
                                col={col}
                                containerHeight={containerHeight}
                                layoutMetrics={layoutMetrics}
                                loadedChart={loadedChart}
                                loadedMeasure={loadedMeasure}
                                maxDensity={maxDensity}
                                densityWindow={props.densityWindow}
                                colors={props.colors}
                                onHover={handleNoteHover}
                                onLeave={handleLeave}
                            />

                            {(props.activations ?? []).map((a: SkillActivation, i: number) => {
                                if (a.time < col.start || a.time >= col.end) return null;
                                const y = (col.end - a.time) * col.scale;
                                const color = a.kind === 'bomb' ? '#c2410c' : '#7c3aed';
                                const label = `${i + 1}. ${a.skill} #${a.copy} · ${a.phase} note #${a.note_number}`;
                                return <g key={`skill-${i}`} role="button" tabIndex={0} aria-label={label}
                                    className="cursor-pointer" onClick={() => props.onSelectSkill(a)}
                                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); props.onSelectSkill(a); } }}>
                                    <title>{label} · measure {a.measure} · {a.time.toFixed(2)}s</title>
                                    <line x1={layoutMetrics.contentOffsetX} x2={layoutMetrics.colWidth - SIDE_MARGIN}
                                        y1={y} y2={y} stroke={color} strokeWidth={props.selectedSkill === a ? 3 : 1.5} strokeDasharray="4 3" />
                                    <rect x={layoutMetrics.contentOffsetX} y={Math.max(0, y - 15)} width={Math.min(180, layoutMetrics.colWidth - 20)} height={14} rx={3} fill={color} />
                                    <text x={layoutMetrics.contentOffsetX + 4} y={Math.max(11, y - 4)} fontSize={10} fill="white">{i + 1}. {a.skill} #{a.copy} · #{a.note_number}</text>
                                </g>;
                            })}

                            {/* Highlight the selected skill's chart time. */}
                            {props.selectedSkill && props.selectedSkill.time >= col.start && props.selectedSkill.time < col.end && (
                                <line 
                                    x1={layoutMetrics.contentOffsetX} 
                                    x2={layoutMetrics.contentOffsetX + (layoutMetrics.laneWidth * LANE_COUNT)}
                                    y1={(col.end - props.selectedSkill.time) * col.scale}
                                    y2={(col.end - props.selectedSkill.time) * col.scale}
                                    stroke="#ef4444" 
                                    strokeWidth="3"
                                />
                            )}
                        </svg>
                        <div className="absolute bottom-0 right-1 text-[9px] text-gray-300 font-mono">{col.start.toFixed(1)}s</div>
                    </div>
                ))}
            </div>
            {hoverTooltip && (
                <div style={{ position: 'fixed', top: hoverTooltip.y - 50, left: hoverTooltip.x + 10, backgroundColor: 'rgba(0,0,0,0.85)', color: 'white', padding: '6px', borderRadius: '4px', fontSize: '12px', zIndex: 9999, pointerEvents: 'none' }}>
                    {hoverTooltip.content}
                </div>
            )}
        </div>
    );
}

export default ChartDetail;
